import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  RadioGroup as MantineRadioGroup,
  RadioGroupProps as MantineRadioGroupProps,
} from '@mantine/core';
import clsx from 'clsx';

import { FormLabel } from '../formlabel/FormLabel';
import { GoogleFontIcon } from '../icons/GoogleFontIcon';
import { SVGIcon } from '../icons/SVGIcon';
import { TextWithTooltip } from '../tooltip/TextWithTooltip';

import { Radio, RadioProps } from './Radio';

import styles from './radio-group.module.scss';

export type RadioGroupOption = RadioProps & {
  description?: ReactNode;
  iconName?: string;
};

export type RadioGroupProps = {
  options: RadioGroupOption[];
  direction?: 'column' | 'row';
  disableRadioGroup?: boolean;
  variant?: 'default' | 'cards';
} & Omit<MantineRadioGroupProps, 'children'>;

export function RadioGroup({
  options,
  label,
  error,
  required,
  direction = 'column',
  disableRadioGroup,
  variant = 'default',
  onChange,
  ...props
}: RadioGroupProps) {
  const { t } = useTranslation();

  return (
    <div
      className={styles['radio-group-container']}
      data-testid="radio-group-container"
    >
      {label && <FormLabel label={label} required={required} />}
      <MantineRadioGroup
        {...props}
        classNames={{
          root: styles['root'],
        }}
        onChange={onChange}
      >
        <div
          className={clsx(
            styles['radiogroup'],
            styles[`radiogroup-${direction}`],
            variant === 'cards' && styles['radiogroup-cards']
          )}
          data-testid="radio-groups"
        >
          {options.map((option) => {
            const { description, iconName, ...radioProps } = option;
            const radio = (
              <Radio
                {...radioProps}
                disabled={option.disabled || disableRadioGroup}
                label={
                  variant === 'cards' ? (
                    <span className={styles['radio-card-text']}>
                      {iconName && (
                        <span className={styles['radio-card-icon-tile']}>
                          <GoogleFontIcon name={iconName} />
                        </span>
                      )}
                      <span className={styles['radio-card-header']}>
                        <TextWithTooltip
                          tooltipProps={{ usePortal: true }}
                          textProps={{
                            span: true,
                            lineClamp: 1,
                            className: styles['radio-card-label'],
                          }}
                        >
                          {option.label}
                        </TextWithTooltip>
                        <span className={styles['radio-card-badge']}>
                          {t('app.radioGroup.current')}
                        </span>
                      </span>
                      {description != null && description !== '' && (
                        <TextWithTooltip
                          tooltipProps={{ usePortal: true }}
                          textProps={{
                            span: true,
                            lineClamp: 3,
                            className: styles['radio-card-description'],
                          }}
                        >
                          {description}
                        </TextWithTooltip>
                      )}
                    </span>
                  ) : (
                    option.label
                  )
                }
              />
            );

            return (
              <div
                key={String(option.value)}
                className={clsx(
                  styles['radio-item'],
                  variant === 'cards' && styles['radio-card']
                )}
                onClick={
                  variant === 'cards'
                    ? () => onChange?.(String(option.value))
                    : undefined
                }
              >
                {radio}
                {variant !== 'cards' && option.labelIconName && (
                  <SVGIcon
                    name={option.labelIconName}
                    className={styles['radio-label-icon']}
                  />
                )}
              </div>
            );
          })}
        </div>
      </MantineRadioGroup>
      {error && <div className={styles['error']}>{error}</div>}
    </div>
  );
}
